import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import api from '@/services/api';
import { zwolnijObrazy } from '@/composables/useChronionyObraz';
import { useToastStore } from '@/stores/toast';

export const useAuthStore = defineStore('auth', () => {
    const user  = ref(null);
    const token = ref(localStorage.getItem('auth_token'));

    const isAuthenticated  = computed(() => !!token.value && !!user.value);
    const isAdmin          = computed(() => user.value?.is_admin ?? false);
    const isSuperAdmin     = computed(() => user.value?.is_super_admin ?? false);
    const canShare         = computed(() => user.value?.can_share ?? false);
    const overlayEnabled   = computed(() => user.value?.overlay_enabled ?? false);
    const userRole         = computed(() => user.value?.role ?? null);

    // Uprawnienia usera (Faza 2c) — źródło gatingu frontu. isAdmin/canShare zostają
    // jako pochodne dla komponentów jeszcze niezmigrowanych.
    const permissions      = computed(() => user.value?.abilities ?? []);
    // Lista znaczy „którekolwiek z tych". Potrzebne tam, gdzie do jednego
    // miejsca prowadzą dwa uprawnienia — np. baza wiedzy: redagowanie ALBO
    // sam odczyt treści wewnętrznych.
    const can = (permission) => Array.isArray(permission)
        ? permission.some(p => permissions.value.includes(p))
        : permissions.value.includes(permission);

    async function login(email, password) {
        // `skipErrorToast`: odmowę pokazuje formularz przy polu e-mail, a nie
        // chmurka w rogu — dwie wiadomości o tym samym tylko rozpraszają.
        const { data } = await api.post('/login', { email, password }, { skipErrorToast: true });
        token.value = data.token;
        user.value  = data.user;
        localStorage.setItem('auth_token', data.token);
        useToastStore().success(`Witaj ponownie, ${data.user.name}!`);
    }

    async function logout() {
        // cichy-catch: wylogowanie i tak czyści stan lokalny, więc nieudane
        // unieważnienie tokenu po stronie serwera nie zmienia tego, co widzi
        // użytkownik. Zatrzymanie go tutaj uwięziłoby go w sesji.
        await api.post('/logout').catch(() => {});
        zwolnijObrazy();
        token.value = null;
        user.value  = null;
        localStorage.removeItem('auth_token');
    }

    async function fetchMe() {
        if (!token.value) return;
        try {
            const { data } = await api.get('/me');
            user.value = data;
        } catch {
            token.value = null;
            localStorage.removeItem('auth_token');
        }
    }

    return { user, token, isAuthenticated, isAdmin, isSuperAdmin, canShare, overlayEnabled, userRole, permissions, can, login, logout, fetchMe };
});
